/**
 * liveChatChannels — les fils du chat du live de l'élève, purs : une puce par tuteur du roster (le
 * tuteur de la séance d'abord), « Tous », et la pastille qui compte les fils
 * que ces puces montrent. Le crochet (`useLiveChat`) les lit ; l'adaptateur des ports aussi, sans
 * tirer le crochet et le magasin avec lui.
 */
import { principalTutorId, rosterTutorIdsOf, type LiveCastSession } from "./liveTutorCast"

/**
 * Un fil du chat : « Tous » (`GROUP_CHANNEL`), ou le fil privé avec un tuteur du roster, désigné par
 * son id. Les ids du serveur sont des nombres : ils ne croisent jamais `group`.
 */
export type LiveChatChannel = string

export const GROUP_CHANNEL = "group"

/** Une puce du chat : son fil, le tuteur à qui il parle (`null` : « Tous »), ses non-lus. */
export type LiveChatChannelEntry = {
    channel: LiveChatChannel
    threadId: string
    tutorId: string | null
    unread: number
}

export type LiveChatSession = {
    thread_id?: string | number | null
    thread_ids?: Record<string, string | number | null | undefined> | null
    tutor?: { id?: number | string | null } | null
    /** Le roster : ses tuteurs ont chacun leur puce, ses élèves disent si le cours est partagé. */
    users?: { id?: number | string | null; is_tutor?: boolean | null }[] | null
    /** Les non-lus par interlocuteur (un tuteur), et ceux du groupe : ce que les puces portent. */
    unread_private_messages_count?: Record<string, number | null | undefined> | null
    unread_messages_count?: number | null
    /** La séance du jour : son remplaçant, s'il y en a un, est le tuteur de la séance (`principalTutorId`). */
    next_occurrences?: LiveCastSession["next_occurrences"]
}

/**
 * Un cours partagé : plus d'un élève au roster. Ce qui en dépend : l'auteur d'un message de « Tous »,
 * pas l'existence de « Tous », qui a toujours sa puce.
 */
export function sharedCourseOf(users: LiveChatSession["users"]): boolean {
    return (users ?? []).filter((member) => member?.is_tutor !== true).length > 1
}

/**
 * Les puces du chat, dans leur ordre : le tuteur de la séance, « Tous » (toujours, même dans un cours
 * à un seul élève : l'ancien chat le montrait à tous, et le tuteur peut y écrire), puis
 * chaque autre tuteur du roster qui a un fil avec l'élève (un remplaçant, un tuteur d'une autre
 * matière) — l'ordre de l'ancien chat, le fil ouvert en moins à sa tête : ici une puce ne bouge pas
 * sous le doigt.
 *
 * Le tuteur de la séance sans fil privé ouvre le fil du groupe, comme aujourd'hui ; un autre tuteur
 * sans fil n'a pas de puce. Chaque puce porte les non-lus du fil qu'elle ouvre : le compte du relevé
 * pour ce tuteur, ou celui du groupe.
 */
export function chatChannels(session: LiveChatSession | null): LiveChatChannelEntry[] {
    if (session == null) return []
    const group = session.thread_id != null ? String(session.thread_id) : null
    const groupUnread = session.unread_messages_count ?? 0
    const privateThread = (tutorId: string) => {
        const thread = session.thread_ids?.[tutorId]
        return thread != null ? String(thread) : null
    }
    const privateUnread = (tutorId: string) => session.unread_private_messages_count?.[tutorId] ?? 0
    const rosterTutors = rosterTutorIdsOf(session)
    // Le tuteur que l'élève a devant lui : le remplaçant du jour, sinon celui de la séance.
    const sessionTutor = principalTutorId(session)

    const entries: LiveChatChannelEntry[] = []
    if (sessionTutor != null) {
        const thread = privateThread(sessionTutor)
        const opened = thread ?? group
        if (opened != null) {
            entries.push({
                channel: sessionTutor,
                threadId: opened,
                tutorId: sessionTutor,
                unread: thread != null ? privateUnread(sessionTutor) : groupUnread,
            })
        }
    }
    if (group != null) {
        entries.push({ channel: GROUP_CHANNEL, threadId: group, tutorId: null, unread: groupUnread })
    }
    for (const tutorId of rosterTutors) {
        if (tutorId === sessionTutor) continue
        const thread = privateThread(tutorId)
        if (thread != null)
            entries.push({ channel: tutorId, threadId: thread, tutorId, unread: privateUnread(tutorId) })
    }
    return entries
}

/**
 * Le fil choisi, s'il a encore sa puce ; sinon la première (le tuteur de la séance). Rien de choisi
 * (`null`) : la première. Sans puce du tout, « Tous » — le fil du groupe, comme le canal du tuteur
 * d'aujourd'hui sans fil privé.
 */
export function resolveChatChannel(session: LiveChatSession | null, chosen: LiveChatChannel | null): LiveChatChannel {
    const entries = chatChannels(session)
    if (chosen != null && entries.some((entry) => entry.channel === chosen)) return chosen
    return entries[0]?.channel ?? GROUP_CHANNEL
}

/** Le fil d'un canal : celui de sa puce ; « Tous » sans puce, le fil du groupe s'il y en a un. */
export function chatThreadOf(session: LiveChatSession | null, channel: LiveChatChannel): string | null {
    const entry = chatChannels(session).find((item) => item.channel === channel)
    if (entry != null) return entry.threadId
    if (channel !== GROUP_CHANNEL) return null
    return session?.thread_id != null ? String(session.thread_id) : null
}

/**
 * La pastille du chat : les non-lus de tous les fils que les puces montrent, chaque fil compté une
 * fois (le tuteur de la séance sans fil privé partage celui du groupe). Un fil qu'aucune puce ne
 * montre — un ancien interlocuteur que le relevé compte encore — n'y entre pas : l'élève n'aurait
 * aucun moyen de l'éteindre.
 */
export function chatUnreadTotal(session: LiveChatSession | null): number {
    const counted = new Map<string, number>()
    for (const entry of chatChannels(session)) counted.set(entry.threadId, entry.unread)
    let total = 0
    for (const unread of counted.values()) total += unread
    return total
}

/**
 * Le canal sur lequel le chat s'ouvre : la première puce qui a des non-lus, dans leur ordre (le
 * tuteur de la séance d'abord) ; rien à lire, le dernier choisi. La pastille ne retombe que quand le
 * fil qui l'a fait monter est relu (`mark-read`) : ouvert ailleurs, elle restait allumée sans rien
 * dire de l'endroit où le message attendait. Chaque puce porte aussi son compte (`<LiveChat channels>`).
 */
export function chatChannelToOpen(session: LiveChatSession | null, current: LiveChatChannel): LiveChatChannel {
    return chatChannels(session).find((entry) => entry.unread > 0)?.channel ?? current
}
